import ApiService from '@/services/api.service'
class OpeningError extends Error {
    constructor(errorCode, message) {
        super(message)
        this.name = this.constructor.name
        this.message = message
        this.errorCode = errorCode
    }
}

const buildOpeningError = (error, fallbackMessage) => {
    const status = error && error.response ? error.response.status : null
    const data = error && error.response ? error.response.data : null
    const message = (data && (data.detail || data.message || data.error)) || error.message || fallbackMessage
    return new OpeningError(status, message)
}

const OpeningService = {

    // OPENING METHODS
    // VIEW METHODS
 
    list: async function() {
        try {
            const response = await ApiService.get("/ouvertures")
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de recuperer les ouvertures.")
        }
    },

    listMine: async function() {
        try {
            const response = await ApiService.get("/ouvertures/miennes")
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de recuperer vos ouvertures.")
        }
    },

    getById: async function(id) {
        try {
            const response = await ApiService.get(`/ouvertures/unique/${id}`)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de recuperer l'ouverture.")
        }
    },


    getAllOuvreur: async function(id) {
        try {
            const response = await ApiService.get(`/ouvertures/ouvreur/all`)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de recuperer les ouvertures.")
        }
    },

    getPublicByToken: async function(token) {
        try {
            const response = await ApiService.get(`/ouvertures/${token}`)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de recuperer l'ouverture.")
        }
    },

    // EDIT METHODS

    create: async function(payload) {
        try {
            const response = await ApiService.post("/ouvertures", payload)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de creer l'ouverture.")
        }
    },

    update: async function(id, payload) {
        try {
            const response = await ApiService.patches(`/ouvertures/${id}`, payload)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de mettre a jour l'ouverture.")
        }
    },

    updateOpeningInfo: async function(id, payload) {
        try {
            const response = await ApiService.patches(`/ouvertures/${id}`, payload)
            return response
        }
        catch (error) {
            throw buildOpeningError(error, "Impossible de mettre a jour les informations de l'ouverture.")
        }
    },

    updateOpeningEvents: async function(id, payload) {
        try {
            const response = await ApiService.patches(`/ouvertures/${id}/events`, payload)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de mettre a jour les creneaux.")
        }
    },

    remove: async function(id) {
        try {
            const response = await ApiService.delete(`/ouvertures/unique/${id}`)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de supprimer l'ouverture.")
        }
    },

    // PARTICIPANT METHODS
    registerParticipant: async function(id, payload) {
        try {
            const response = await ApiService.post(`/ouvertures/inscription/${id}`, payload)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de s'inscrire a l'ouverture.")
        }
    },

    addParticipant: async function(id, payload) {
        try {
            const response = await ApiService.post(`/ouvertures/${id}/participants`, payload)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible d'ajouter le participant.")
        }
    },

    updateParticipant: async function(token, participantId, payload) {
        try {
            const response = await ApiService.put(`/ouvertures/${token}/participants/${participantId}`, payload)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de mettre a jour le participant.")
        }
    },

    addParticipantSailUsed: async function(openingId, participantId, payload) {
        try {
            const response = await ApiService.post(
                `/ouvertures/${openingId}/participants/${participantId}/sails-used`,
                payload
            )
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible d'ajouter la voile utilisee.")
        }
    },

    addParticipantBoardUsed: async function(openingId, participantId, payload) {
        try {
            const response = await ApiService.post(
                `/ouvertures/${openingId}/participants/${participantId}/boards-used`,
                payload
            )
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible d'ajouter la planche utilisee.")
        }
    },

    clearParticipantCurrentSail: async function(openingId, participantId) {
        try {
            const response = await ApiService.delete(
                `/ouvertures/${openingId}/participants/${participantId}/current-sail`
            )
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de vider la voile courante.")
        }
    },

    clearParticipantCurrentBoard: async function(openingId, participantId) {
        try {
            const response = await ApiService.delete(
                `/ouvertures/${openingId}/participants/${participantId}/current-board`
            )
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de vider la planche courante.")
        }
    },

    removeParticipantSailUsed: async function(openingId, participantId, sailId) {
        try {
            const response = await ApiService.delete(
                `/ouvertures/${openingId}/participants/${participantId}/sails-used/${sailId}`
            )
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de supprimer la voile utilisee.")
        }
    },

    removeParticipantBoardUsed: async function(openingId, participantId, boardId) {
        try {
            const response = await ApiService.delete(
                `/ouvertures/${openingId}/participants/${participantId}/boards-used/${boardId}`
            )
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de supprimer la planche utilisee.")
        }
    },

    removeParticipant: async function(token, participantId) {
        try {
            const response = await ApiService.delete(`/ouvertures/${token}/participants/${participantId}`)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de supprimer le participant.")
        }
    },

    // PREREGISTRATION METHODS (ouvreur)
    validatePreregistration: async function(openingId, preId) {
        try {
            const response = await ApiService.post(`/ouvertures/${openingId}/preregistrations/${preId}/validate`)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de valider la préinscription.")
        }
    },

    updatePreregistration: async function(openingId, preId, payload) {
        try {
            const response = await ApiService.put(`/ouvertures/${openingId}/preregistrations/${preId}`, payload)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de mettre à jour la préinscription.")
        }
    },

    removePreregistration: async function(openingId, preId) {
        try {
            const response = await ApiService.delete(`/ouvertures/${openingId}/preregistrations/${preId}`)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de supprimer la préinscription.")
        }
    },

    getMyRegistration: async function(id) {
        try {
            const response = await ApiService.get(`/mes-inscriptions`)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de recuperer votre inscription.")
        }
    },

    getMyHistory: async function() {
        try {
            const response = await ApiService.get("/mes-inscriptions/historique")
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de récupérer votre historique.")
        }
    },

    cancelRegistration: async function(id) {
        try {
            const response = await ApiService.delete(`/mes-inscriptions/${id}`)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible d'annuler votre inscription.")
        }
    },

    updateMyRegistration: async function(id, payload) {
        try {
            const response = await ApiService.put(`/mes-inscriptions/${id}`, payload)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de mettre a jour votre inscription.")
        }
    },

    // COVOIT METHODS 
    listCovoits: async function(ouvertureId) {
        try {
            const response = await ApiService.get(`/ouvertures/${ouvertureId}/covoits`)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de recuperer les covoiturages.")
        }
    },

    getMyCovoits: async function() {
        try {
            const response = await ApiService.get("/ouvertures/covoits/mes")
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de recuperer vos covoiturages.")
        }
    },

    createCovoit: async function(ouvertureId, payload) {
        try {
            const response = await ApiService.post(`/ouvertures/${ouvertureId}/covoits`, payload)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de creer le covoiturage.")
        }
    },

    updateCovoit: async function(ouvertureId, covoitId, payload) {
        try {
            const response = await ApiService.put(`/ouvertures/${ouvertureId}/covoits/${covoitId}`, payload)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de mettre a jour le covoiturage.")
        }
    },

    deleteCovoit: async function(ouvertureId, covoitId) {
        try {
            const response = await ApiService.delete(`/ouvertures/${ouvertureId}/covoits/${covoitId}`)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible d'annuler le covoiturage.")
        }
    },

    joinCovoit: async function(ouvertureId, covoitId) {
        try {
            const response = await ApiService.post(`/ouvertures/${ouvertureId}/covoits/${covoitId}/join`)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de rejoindre le covoiturage.")
        }
    },

    leaveCovoit: async function(ouvertureId, covoitId) {
        try {
            const response = await ApiService.post(`/ouvertures/${ouvertureId}/covoits/${covoitId}/leave`)
            return response
        } catch (error) {
            throw buildOpeningError(error, "Impossible de quitter le covoiturage.")
        }
    }

}

export default OpeningService

export { OpeningService, OpeningError }
